import type { IMediaFile } from "@/shared/types";
import { useGetMediaFoldersQuery } from "@api/media-folders";
import {
  MediaLibCategoryEnum,
  MediaLibFilesNotFound,
  useMediaLibUploadingStore,
  useMediaLibWebsiteStore,
  type IMediaLibDialogItem,
  type MediaLibPresetEnum,
  type TElement,
} from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { nanoid } from "@reduxjs/toolkit";
import { useEffect, useRef, type UIEvent } from "react";
import { MediaLibFilesSkeleton } from "../../files/media-lib-files-skeleton";
import { useMediaLibPanelFetchFiles } from "../hooks";
import {
  useMediaLibPanelStore,
  useMediaLibPanelStoreShallow,
} from "../stores/media-lib-panel.store";
import { MediaLibPanelFolders } from "./folders/media-lib-panel-folders";
import { MediaLibDialogFilesBulkActions } from "./media-lib-dialog-files-bulk-actions";
import { MediaLibPanelCategories } from "./media-lib-panel-categories";
import { MediaLibPanelError } from "./media-lib-panel-error";
import { MediaLibPanelFiles } from "./media-lib-panel-files";
import { MediaLibPanelNoFiles } from "./media-lib-panel-no-files";
import { MediaLibPanelSearchInput } from "./media-lib-panel-search-input";

interface IProps {
  fromPanel?: "images" | "videos";
  view?: "panel" | "dialog";
  onSelect?: (item: IMediaFile) => void;
  selectedItems?: IMediaLibDialogItem[];
  isMultiSelect?: boolean;
  preset?: MediaLibPresetEnum;
  elementWrapper?: React.ComponentType<{
    children: React.ReactNode;
    element: TElement;
  }>;
  source?: "default" | "settings";
}

export const MediaLibPanel = ({ fromPanel, ...props }: IProps) => {
  const uploadingFiles = useMediaLibUploadingStore((s) => s.uploadingFiles);
  const {
    setPanelId,
    hasAnyFiles,
    reset,
    setCategory,
    category,
    hasSearch,
    selectedFolderId,
    bulkUpdateFiles,
    bulkDeleteFiles,
  } = useMediaLibPanelStoreShallow((s) => ({
    setPanelId: s.setPanelId,
    hasSearch: s.searchQuery.length > 0,
    hasAnyFiles: s.files.length + uploadingFiles.length > 0,
    reset: s.reset,
    setCategory: s.setCategory,
    category: s.category,
    selectedFolderId: s.selectedFolderId,
    bulkUpdateFiles: s.bulkUpdateFiles,
    bulkDeleteFiles: s.bulkDeleteFiles,
  }));

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const canScrollFetch = useRef(true);
  const { isError: isFoldersError, refetch: reloadFolders } =
    useGetMediaFoldersQuery(
      { archived: false },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const {
    fetchMoreFiles,
    shouldShowSkeleton,
    isError: isFilesError,
    reloadData,
  } = useMediaLibPanelFetchFiles();

  useEffect(() => {
    const panelId = nanoid();
    setPanelId(panelId);

    if (useMediaLibUploadingStore.getState().uploadingFiles.length > 0) {
      setCategory(MediaLibCategoryEnum.AllFiles);
    } else if (useMediaLibWebsiteStore.getState().websiteUrl != null) {
      setCategory(MediaLibCategoryEnum.Website);
    } else {
      if (fromPanel === "images") {
        setCategory(MediaLibCategoryEnum.Images);
      } else if (fromPanel === "videos") {
        setCategory(MediaLibCategoryEnum.Videos);
      }
    }

    return () => {
      if (useMediaLibPanelStore.getState().panelId === panelId) {
        reset();
      }
    };
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [category, selectedFolderId]);

  const handleScroll = (e: UIEvent<HTMLDivElement, globalThis.UIEvent>) => {
    const { clientHeight, scrollTop, scrollHeight } = e.currentTarget;
    if (scrollHeight - clientHeight <= 0) {
      return;
    }

    if (scrollTop >= scrollHeight - clientHeight - 125) {
      if (canScrollFetch.current) {
        canScrollFetch.current = false;

        fetchMoreFiles();
      }
    } else {
      canScrollFetch.current = true;
    }
  };

  const hasError = isFilesError || isFoldersError;
  const hasNoSearchResults = !hasAnyFiles && hasSearch;
  const hasNoFiles = !hasAnyFiles && !hasSearch;
  const isWebsiteOrImport = [
    MediaLibCategoryEnum.Website,
    MediaLibCategoryEnum.Import,
  ].includes(category);
  const shouldShowFilesLabel = !(isWebsiteOrImport && !hasAnyFiles);

  return (
    <>
      <div className="tw-flex tw-flex-col tw-overflow-hidden">
        <MediaLibPanelSearchInput />
        {!hasSearch && <MediaLibPanelCategories />}

        <div
          className="scrollbar tw-mr-1 tw-mt-1 tw-grid tw-flex-1 tw-grid-rows-[auto_1fr] tw-content-start tw-overflow-y-scroll tw-overscroll-contain"
          onScroll={handleScroll}
          ref={scrollContainerRef}
        >
          {!hasSearch && !hasError && <MediaLibPanelFolders />}

          <div
            className={cn(
              "tw-h-full tw-pb-6 tw-pl-6 tw-pr-4 -sm:tw-pl-3 -sm:tw-pr-2 -sm:tw-pb-3",
              hasSearch && "tw-mt-3",
            )}
          >
            {shouldShowFilesLabel && (
              <div className="tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-font-medium">
                <span>{!hasSearch ? "Files" : "Files from My library"}</span>
              </div>
            )}

            {hasError ? (
              <MediaLibPanelError
                onReload={() => {
                  reloadData();
                  reloadFolders();
                }}
              />
            ) : shouldShowSkeleton ? (
              <MediaLibFilesSkeleton className="tw-mt-4" />
            ) : hasNoSearchResults ? (
              <MediaLibFilesNotFound />
            ) : hasNoFiles ? (
              <MediaLibPanelNoFiles fromPanel={fromPanel} />
            ) : (
              <MediaLibPanelFiles {...props} />
            )}
          </div>
        </div>
      </div>
      {(props.isMultiSelect || props.source === "settings") && (
        <MediaLibDialogFilesBulkActions
          bulkUpdateFiles={bulkUpdateFiles}
          bulkDeleteFiles={bulkDeleteFiles}
        />
      )}
    </>
  );
};
